import clsx from "clsx";
import React from "react";
import { FC } from "react";
import { useMemo } from "react";
import { FieldRenderProps } from "react-final-form";
import { Theme } from "@material-ui/core/styles";
import { InputBaseProps } from "@material-ui/core/InputBase";
import InputBase from "@material-ui/core/InputBase";
import InputLabel from "@material-ui/core/InputLabel";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { lighten } from "@material-ui/core/styles/colorManipulator";
import Typography from "@material-ui/core/Typography";
import InfoIcon from "../InfoIcon";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingBottom: 21,
    marginBottom: -18,
    "label + &": {
      marginTop: 4,
    },
  },
  input: {
    lineHeight: 1,
    fontSize: 16,
    borderRadius: 15,
    opacity: 1,
    width: "100%",
    position: "relative",
    border: "1px solid #ced4da",
    padding: "12px 16px 11px 16px",
    boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.04)",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
      borderColor: "#7d797a",
    },
    "&::placeholder": {
      color: "#bcbcbc",
      opacity: 1,
    },
    "&:-ms-input-placeholder": {
      color: "#bcbcbc",
    },
    "&::-ms-input-placeholder": {
      color: "#bcbcbc",
    },
    "&::-ms-reveal": {
      display: "none",
    },
  },
  error: {
    "& input": {
      borderRadius: 15,
      border: `1px solid ${theme.palette.error.main}`,
      boxShadow: `inset 0 1px 2px 0 ${lighten(theme.palette.error.main, 0.9)}`,
      "&:focus": {
        borderColor: theme.palette.error.main,
      },
    },
    "& > textarea": {
      borderRadius: 15,
      border: `1px solid ${theme.palette.error.main}`,
      boxShadow: `inset 0 1px 2px 0 ${lighten(theme.palette.error.main, 0.9)}`,
      "&:focus": {
        borderColor: theme.palette.error.main,
      },
    },
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: "#a1a4ac",
    "& > span": {
      lineHeight: "inherit",
    },
  },
  helperText: {
    height: 0,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    color: "#a1a4ac",
  },
  hasError: {
    color: theme.palette.error.main,
  },
}));

const BootstrapInput: FC<FieldRenderProps<InputBaseProps>> = ({
  regexp,
  maxLength,
  onChange,
  input,
  InputProps,
  label,
  classes,
  helperText,
  infoIcon,
  meta: {
    dirtySinceLastSubmit,
    submitFailed,
    invalid,
    error,
    submitError,
  } = {},
  ...rest
}) => {
  const localClasses = useStyles();

  const hasError = useMemo(
    () => invalid && submitFailed && !dirtySinceLastSubmit,
    [invalid, dirtySinceLastSubmit, submitFailed],
  );

  const handleOnchange = React.useCallback((e) => {
    if (
      e.target.value.length &&
      ((maxLength && e.target.value.length > maxLength) ||
        (regexp && !regexp.test(e.target.value)))
    )
      return;
    if (input && typeof input.onChange == "function") input.onChange(e);
    if (typeof onChange == "function") onChange(e.target.value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {!!label && (
        <InputLabel className={localClasses.label}>
          {label}
          {rest.required && (
            <Typography
              className={clsx(
                localClasses.label,
                hasError && localClasses.hasError,
              )}
              component={"span"}
              variant={"subtitle2"}
            >
              {" "}
              *
            </Typography>
          )}
          {infoIcon && <InfoIcon text={infoIcon} />}
        </InputLabel>
      )}
      <InputBase
        {...input}
        onChange={handleOnchange}
        {...rest}
        error={hasError}
        classes={{
          root: localClasses.root,
          input: localClasses.input,
          error: localClasses.error,
          ...(classes && classes),
        }}
      />
      {((hasError && (error || submitError)) || !!helperText) && (
        <InputLabel
          className={clsx(
            localClasses.helperText,
            hasError && localClasses.hasError,
          )}
        >
          {(hasError && ((error === 1 ? "" : error) || submitError)) ||
            helperText}
        </InputLabel>
      )}
    </div>
  );
};

export default React.memo(BootstrapInput);
